/**
 * Ledger Service — org-scoped chart of accounts (FlowState SoR)
 * Personal cashbook remains in accounting.service.ts
 *
 * @module services/ledger.service
 * @since 2026-07-24
 */
import { supabase } from '../integrations/supabase/client';
import type { ServiceResponse, IService } from './service-registry';
import { logger } from '../lib/logger';
import type {
  CreateLedgerAccountInput,
  LedgerAccount,
  LedgerTaxRate,
  SeedLedgerResult,
  UpdateLedgerAccountInput,
} from '@flowstate/types';

export interface ILedgerService extends IService {
  listAccounts(organizationId: string, options?: { status?: 'ACTIVE' | 'ARCHIVED' | 'all' }): Promise<ServiceResponse<LedgerAccount[]>>;
  getAccount(accountId: string): Promise<ServiceResponse<LedgerAccount>>;
  createAccount(input: CreateLedgerAccountInput): Promise<ServiceResponse<LedgerAccount>>;
  updateAccount(accountId: string, input: UpdateLedgerAccountInput): Promise<ServiceResponse<LedgerAccount>>;
  archiveAccount(accountId: string): Promise<ServiceResponse<LedgerAccount>>;
  listTaxRates(organizationId: string): Promise<ServiceResponse<LedgerTaxRate[]>>;
  seedDefaultAccounts(organizationId: string): Promise<ServiceResponse<SeedLedgerResult>>;
}

export class LedgerService implements ILedgerService {
  readonly name = 'ledger';

  async initialize(): Promise<void> {
    logger.info('Ledger service initialized');
  }

  async dispose(): Promise<void> {
    logger.info('Ledger service disposed');
  }

  async listAccounts(
    organizationId: string,
    options: { status?: 'ACTIVE' | 'ARCHIVED' | 'all' } = {}
  ): Promise<ServiceResponse<LedgerAccount[]>> {
    try {
      let query = supabase
        .from('ledger_accounts')
        .select('*')
        .eq('organization_id', organizationId)
        .order('code', { ascending: true });

      const status = options.status ?? 'ACTIVE';
      if (status !== 'all') {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) {
        return { success: false, error: error.message, code: 'LEDGER_ACCOUNTS_FETCH_FAILED', service: this.name };
      }
      return { success: true, data: (data || []) as LedgerAccount[] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        code: 'LEDGER_ACCOUNTS_FETCH_ERROR',
        service: this.name,
      };
    }
  }

  async getAccount(accountId: string): Promise<ServiceResponse<LedgerAccount>> {
    try {
      const { data, error } = await supabase
        .from('ledger_accounts')
        .select('*')
        .eq('id', accountId)
        .single();
      if (error || !data) {
        return {
          success: false,
          error: error?.message || 'Ledger account not found',
          code: 'LEDGER_ACCOUNT_NOT_FOUND',
          service: this.name,
        };
      }
      return { success: true, data: data as LedgerAccount };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        code: 'LEDGER_ACCOUNT_FETCH_ERROR',
        service: this.name,
      };
    }
  }

  async createAccount(input: CreateLedgerAccountInput): Promise<ServiceResponse<LedgerAccount>> {
    try {
      const { data, error } = await supabase
        .from('ledger_accounts')
        .insert({
          organization_id: input.organization_id,
          code: input.code,
          name: input.name,
          type: input.type,
          tax_type: input.tax_type ?? null,
          description: input.description ?? null,
          status: input.status ?? 'ACTIVE',
        })
        .select('*')
        .single();
      if (error) {
        return { success: false, error: error.message, code: 'LEDGER_ACCOUNT_CREATE_FAILED', service: this.name };
      }
      return { success: true, data: data as LedgerAccount };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        code: 'LEDGER_ACCOUNT_CREATE_ERROR',
        service: this.name,
      };
    }
  }

  async updateAccount(
    accountId: string,
    input: UpdateLedgerAccountInput
  ): Promise<ServiceResponse<LedgerAccount>> {
    try {
      const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (input.code !== undefined) updateData.code = input.code;
      if (input.name !== undefined) updateData.name = input.name;
      if (input.type !== undefined) updateData.type = input.type;
      if (input.tax_type !== undefined) updateData.tax_type = input.tax_type;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.status !== undefined) updateData.status = input.status;

      const { data, error } = await supabase
        .from('ledger_accounts')
        .update(updateData)
        .eq('id', accountId)
        .select('*')
        .single();
      if (error) {
        return { success: false, error: error.message, code: 'LEDGER_ACCOUNT_UPDATE_FAILED', service: this.name };
      }
      return { success: true, data: data as LedgerAccount };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        code: 'LEDGER_ACCOUNT_UPDATE_ERROR',
        service: this.name,
      };
    }
  }

  async archiveAccount(accountId: string): Promise<ServiceResponse<LedgerAccount>> {
    return this.updateAccount(accountId, { status: 'ARCHIVED' });
  }

  async listTaxRates(organizationId: string): Promise<ServiceResponse<LedgerTaxRate[]>> {
    try {
      const { data, error } = await supabase
        .from('ledger_tax_rates')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name', { ascending: true });
      if (error) {
        return { success: false, error: error.message, code: 'LEDGER_TAX_RATES_FETCH_FAILED', service: this.name };
      }
      return { success: true, data: (data || []) as LedgerTaxRate[] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        code: 'LEDGER_TAX_RATES_FETCH_ERROR',
        service: this.name,
      };
    }
  }

  async seedDefaultAccounts(organizationId: string): Promise<ServiceResponse<SeedLedgerResult>> {
    try {
      const { data, error } = await supabase.rpc('seed_default_ledger_accounts', {
        p_org_id: organizationId,
      });
      if (error) {
        return { success: false, error: error.message, code: 'LEDGER_SEED_FAILED', service: this.name };
      }
      const result = data as SeedLedgerResult;
      logger.info('Ledger seeded', { organizationId, result });
      return { success: true, data: result };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        code: 'LEDGER_SEED_ERROR',
        service: this.name,
      };
    }
  }
}

export const ledgerService = new LedgerService();
